import {ActionIcon, Group, Select, Stack, Text, Tooltip} from '@mantine/core';
import React from 'react';

import {FieldMigrationMapping} from 'antibodies/schema-configuration/schema-migration/AntibodySchemaMigrationConstants';
import type {useStyles} from 'antibodies/schema-configuration/schema-migration/NewFieldsMigrationPreview';
import {ClientSchemaField} from 'registry/field/FieldTable';
import getIconAndLabel from 'registry/field/GetFieldLabelAndIcon';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {SchemaFieldSelector} from 'schemas/SchemaFieldSelectorConstants';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

type MigrationClasses = ReturnType<typeof useStyles>['classes'];

function getFieldTypeLabel(
  field: ClientSchemaField,
  schemaFieldSelectors: Array<SchemaFieldSelector>,
  classes: MigrationClasses
): JSX.Element {
  const {icon, label} = getIconAndLabel({
    field,
    schemaFieldSelectors,
    inEditMode: false,
  });
  return (
    <Group spacing="xs" align="center" noWrap={true}>
      <BenchlingIcon icon={icon} className={classes.iconStyle} />
      {label}
    </Group>
  );
}

// Sentinel value for the explicit "— none —" select item, distinct from both a real
// allowed-option name and an implicitly-unmapped option (which has no entry in the map and shows
// the placeholder instead). A leading null char is used because a real option name can never
// contain one, so this can never collide with a legitimate value.
const NONE_OPTION_VALUE = '\0none';

interface OptionMappingPanelProps {
  sourceOptions: Array<{id: string; name: string}>;
  allowedOptions: Array<string>;
  destinationFieldName: string;
  // Keyed by source option id. A present key (even with a null value = explicit "— none —") means the
  // option has been acknowledged; an absent key means it is still implicitly unmapped.
  currentMappings: Map<string, string | null>;
  classes: MigrationClasses;
  onMappingChange: (sourceOptionId: string, destOptionName: string | null) => void;
}

function OptionMappingPanel({
  sourceOptions,
  allowedOptions,
  destinationFieldName,
  currentMappings,
  classes,
  onMappingChange,
}: OptionMappingPanelProps): JSX.Element {
  const destSelectData = [
    {value: NONE_OPTION_VALUE, label: '— none —'},
    ...allowedOptions.map((name) => ({value: name, label: name})),
  ];
  return (
    <Stack spacing="xs">
      {sourceOptions.map((sourceOpt) => {
        const isAcknowledged = currentMappings.has(sourceOpt.id);
        const mappedName = currentMappings.get(sourceOpt.id) ?? null;
        // Explicit "— none —" maps to the sentinel; a real name maps to itself; an unacknowledged
        // option has no value so the Select shows its placeholder.
        const selectValue = isAcknowledged ? (mappedName ?? NONE_OPTION_VALUE) : null;
        return (
          <Group key={sourceOpt.id} spacing="xs" noWrap={true} align="center">
            <Text size="sm" truncate={true} className={classes.optionSourceName} title={sourceOpt.name}>
              {sourceOpt.name}
            </Text>
            <Text size="sm" color="dimmed" className={classes.optionArrow}>
              →
            </Text>
            <Select
              size="xs"
              // Scope the accessible name by destination field too: multiple dropdown fields can be
              // expanded at once, and keying only by source option name would produce duplicate
              // accessible names (ambiguous for screen readers and role/name queries).
              aria-label={`Destination option for ${sourceOpt.name} in ${destinationFieldName}`}
              data={destSelectData}
              value={selectValue}
              placeholder="Select an option"
              error={!isAcknowledged}
              onChange={(val) => {
                onMappingChange(sourceOpt.id, val === NONE_OPTION_VALUE ? null : val);
              }}
              className={classes.optionSelect}
            />
          </Group>
        );
      })}
    </Stack>
  );
}

export interface NewFieldMigrationRowProps {
  destField: ClientSchemaField;
  allowedOptions: Array<string>;
  isMigratable: boolean;
  currentMapping: FieldMigrationMapping | undefined;
  sourceOptions: Array<{id: string; name: string}> | null;
  matchingFields: Array<SchemaField | ClientSchemaField>;
  isExpanded: boolean;
  hasUnmapped: boolean;
  showSourceColumn: boolean;
  schemaFieldSelectors: Array<SchemaFieldSelector>;
  classes: MigrationClasses;
  onSourceFieldChange: (sourceFieldId: string | null) => void;
  onExpandToggle: () => void;
  onOptionMappingChange: (sourceOptionId: string, destOptionName: string | null) => void;
}

export default function NewFieldMigrationRow({
  destField,
  allowedOptions,
  isMigratable,
  currentMapping,
  sourceOptions,
  matchingFields,
  isExpanded,
  hasUnmapped,
  showSourceColumn,
  schemaFieldSelectors,
  classes,
  onSourceFieldChange,
  onExpandToggle,
  onOptionMappingChange,
}: NewFieldMigrationRowProps): JSX.Element {
  const sourceFieldId = currentMapping?.sourceFieldId ?? null;
  // Only fields with an apiId can be a Select option (the value is the apiId), so drop any without
  // one rather than asserting; disabled then reflects whether any selectable source actually remains.
  const sourceFieldOptions = matchingFields
    .filter((f): f is typeof f & {apiId: string} => f.apiId != null)
    .map((f) => ({value: f.apiId, label: f.displayName}));

  return (
    <>
      <tr>
        <td>{destField.name}</td>
        <td className={classes.definitionCell}>
          {getFieldTypeLabel(destField, schemaFieldSelectors, classes)}
        </td>
        <td>{destField.isRequired ? 'Required' : ''}</td>
        {showSourceColumn && (
          <td>
            {isMigratable && (
              <Group spacing="xs" noWrap={true}>
                <Select
                  aria-label={`Source field for ${destField.name}`}
                  data={sourceFieldOptions}
                  value={sourceFieldId}
                  onChange={(val) => {
                    onSourceFieldChange(val);
                  }}
                  clearable={true}
                  placeholder="None"
                  disabled={sourceFieldOptions.length === 0}
                />
                {sourceFieldId != null &&
                  allowedOptions.length > 0 && (
                    // One tooltip spans both the caret and the warning triangle. When options are still
                    // unmapped it explains the problem; otherwise it just labels the expand/collapse action.
                    <Tooltip
                      label={
                        hasUnmapped
                          ? 'Some source options aren’t mapped yet. Choose a destination option for each, ' +
                            'or “— none —” to clear it.'
                          : isExpanded
                            ? 'Collapse option mappings'
                            : 'Map options'
                      }
                      multiline={hasUnmapped}
                      width={hasUnmapped ? 240 : undefined}
                      withArrow={true}
                    >
                      <Group spacing="xs" noWrap={true}>
                        <ActionIcon
                          size="sm"
                          // The human-readable description lives on the surrounding Tooltip; this
                          // aria-label keeps the button findable for assistive tech and tests.
                          aria-label={`Map options for ${destField.name}`}
                          // "default" (bordered) rather than "subtle" so the control reads as a button
                          // at rest instead of a static icon.
                          variant="default"
                          onClick={() => {
                            onExpandToggle();
                          }}
                        >
                          <BenchlingIcon
                            icon={isExpanded ? 'chevron-up' : 'chevron-down'}
                            className={classes.iconStyle}
                          />
                        </ActionIcon>
                        {/* Show a warning triangle beside the caret (not in place of it) while any options
                        still need review, so the caret stays a normal expand/collapse affordance. */}
                        {hasUnmapped && <BenchlingIcon icon="warning" className={classes.warningIcon} />}
                      </Group>
                    </Tooltip>
                  )}
              </Group>
            )}
          </td>
        )}
      </tr>

      {isExpanded && sourceOptions != null && allowedOptions.length > 0 && (
        <tr key={`${destField.name}-options`} className={classes.optionRow}>
          {/* Empty cells so the mapping panel lines up under the Source field column. */}
          <td />
          <td />
          <td />
          <td className={classes.optionCell}>
            <OptionMappingPanel
              sourceOptions={sourceOptions}
              allowedOptions={allowedOptions}
              destinationFieldName={destField.name}
              classes={classes}
              currentMappings={
                new Map(
                  (currentMapping?.optionValueMappings ?? []).map(
                    ({sourceOptionId, destinationOptionName}) => [sourceOptionId, destinationOptionName]
                  )
                )
              }
              onMappingChange={(sourceOptionId, destOptionName) => {
                onOptionMappingChange(sourceOptionId, destOptionName);
              }}
            />
          </td>
        </tr>
      )}
    </>
  );
}
